import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { queryClient } from '@/queries';
import { quotesQueryKeys } from '@/queries/quotes/quotes.keys';
import { getQuoteReviewerData, seedQuoteReviewerData } from '@/queries/quotes/quotes.utils';
import { MINUTE_IN_MS } from '@/constants/date.constants';
import { Uuid } from '@/api/types';

const getRouteWithReviewer = async (routeQuoteId: Uuid) => {
  const data = await api.quote.getRouteQuoteCustomer(routeQuoteId);

  await seedQuoteReviewerData(data.caseId, routeQuoteId);

  return data;
};

export const quoteQueries = {
  routeQuote: (routeQuoteId?: string) =>
    queryOptions({
      queryKey: quotesQueryKeys.getQuote(routeQuoteId),
      queryFn: () => (routeQuoteId ? api.quote.getRouteQuote(routeQuoteId) : null),
      enabled: !!routeQuoteId,
    }),
  routeQuoteCustomer: (routeQuoteId: Uuid) =>
    queryOptions({
      queryKey: quotesQueryKeys.getCustomerQuote(routeQuoteId),
      queryFn: () => getRouteWithReviewer(routeQuoteId),
      staleTime: MINUTE_IN_MS * 10,
    }),
  getQuoteReviewerData: (caseId: Uuid, routeQuoteId: Uuid) =>
    queryOptions({
      queryKey: quotesQueryKeys.getCustomerQuote(caseId, routeQuoteId),
      queryFn: () => getQuoteReviewerData(caseId),
      staleTime: Infinity,
    }),
};

const useGetQuoteByIdQuery = (routeQuoteId?: string) => {
  return useQuery(quoteQueries.routeQuote(routeQuoteId));
};

const useRouteQuoteByIdCustomerQuery = (routeQuoteId?: Uuid | null, enabled: boolean = true) => {
  return useQuery({
    ...quoteQueries.routeQuoteCustomer(routeQuoteId as Uuid),
    enabled: !!routeQuoteId && enabled,
  });
};

const useQuoteReviewerDataByInfoQuery = (caseId: Uuid, routeQuoteId: Uuid) => {
  return useQuery(quoteQueries.getQuoteReviewerData(caseId, routeQuoteId));
};

export const invalidateQuoteQueries = () => queryClient.invalidateQueries({ queryKey: quotesQueryKeys.getQuote() });

export { useGetQuoteByIdQuery, useRouteQuoteByIdCustomerQuery, useQuoteReviewerDataByInfoQuery };
